/**
 * 확장 능력 조회 (ADR 0024)
 * --------------------------------------------------------------------------
 * `GET /api/v1/capabilities` 하나로 서버가 등록한 확장을 받습니다. 화면마다 묻지
 * 않고 앱 전체에서 한 번만 캐시합니다 — 값이 배포 사이에 바뀌지 않기 때문입니다.
 *
 * 호출부는 **세 상태를 구분**해야 합니다. 아직 모름(pending) · 확인 실패(error) ·
 * 확인됨(ready)이고, 앞의 둘에서는 확장 기여를 노출하지 않습니다(fail closed).
 *
 * 브라우저의 유일한 통로는 여전히 `@/api/client`입니다.
 */
import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ExtensionCapabilitiesResponse } from "@k8s-dashboard/contracts";
import { apiGet } from "./client";

export const capabilityKeys = { extensions: ["extension-capabilities"] as const };

/** 능력 조회의 세 상태. 값이 없는 두 상태를 하나로 뭉개지 않습니다. */
export type ExtensionCapabilitySnapshot =
  | { status: "pending" }
  | { status: "error" }
  | { status: "ready"; response: ExtensionCapabilitiesResponse };

export function useExtensionCapabilities() {
  return useQuery({
    queryKey: capabilityKeys.extensions,
    queryFn: ({ signal }) => apiGet<ExtensionCapabilitiesResponse>("/api/v1/capabilities", {}, signal),
    /* 배포 사이에 바뀌지 않는 값입니다. 화면 전환마다 다시 묻지 않습니다. */
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });
}

/** 능력 상태와 재조회. nav·팔레트·확장 라우트가 모두 이 하나를 봅니다. */
export function useExtensionCapabilitySnapshot(): { snapshot: ExtensionCapabilitySnapshot; refetch: () => void } {
  const query = useExtensionCapabilities();
  const { data, isError, refetch } = query;
  /* 스냅숏 정체성을 고정합니다 — 렌더마다 새 객체를 주면 nav가 매번 다시 계산됩니다. */
  const snapshot = useMemo<ExtensionCapabilitySnapshot>(
    () => (data ? { status: "ready", response: data } : isError ? { status: "error" } : { status: "pending" }),
    [data, isError],
  );
  return { snapshot, refetch: useCallback(() => void refetch(), [refetch]) };
}
